'use client';
import BrandLogo from '@/views/commonComponent/brand/BrandLogo';
import { useState, useEffect } from 'react';
import type { FormInstance } from 'antd';
import { Button, Checkbox, Form, Input } from 'antd';
import { Container, Row, Col } from 'react-bootstrap';
import { auth } from '../../../fire-base/FirebaseAuth';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { emailPattern } from '../../../utils/FormValidations';
import Link from 'next/link';
import '../style.scss';
import { useRouter } from 'next/navigation';
import { toaster } from '@/utils/toast';
import { appAction } from '@/store/store';

const LoginButton = ({ form }: { form: FormInstance }) => {
  const [submittable, setSubmittable] = useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (localStorage.getItem('response')) {
      localStorage.removeItem('response');
    }
    if (localStorage.getItem('user')) {
      localStorage.removeItem('user');
    }
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values, form]);

  return (
    <Button className="primaryButton" type="primary" htmlType="submit" disabled={!submittable}>
      Login
    </Button>
  );
};

type loginData = {
  email: string;
  password: string;
  remember: boolean;
};

const Login = () => {
  const [form] = Form.useForm();
  const router = useRouter();
  const getUser = appAction((action) => action.AuthModel.getUserObj);

  const getUserVal = () => {
    getUser().then((val: any) => {
      val === 'Not found'
        ? toaster('error', 'Account not found please signup')
        : router.replace('/dashboard');
    });
  };

  const onFinish = (values: loginData) => {
    signInWithEmailAndPassword(auth, values.email, values.password)
      .then((res) => {
        toaster('success', 'Logged in successfully');
        // Hydrate userObj via getUserVal before routing to /dashboard so
        // protected pages render with a populated store on the first
        // tick (matches the OAuth path below). Unverified users still
        // bypass to the email-verify page where /user isn't required.
        if (res.user.emailVerified) {
          getUserVal();
        } else {
          router.push('/auth/email-verify');
        }
      })
      .catch((e) => {
        toaster('error', 'Invalid credentials');
      });
  };

  const onFinishFailed = (errorInfo: any) => {
    toaster('error', 'Please try again with valid values');
  };

  const handleGoogleLogin = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then(() => {
        getUserVal();
      })
      .catch(() => {
        toaster('error', 'Login failed');
      });
  };
  const handleMicroSoftLogin = () => {
    const provider = new OAuthProvider('microsoft.com');
    signInWithPopup(auth, provider)
      .then(() => {
        getUserVal();
      })
      .catch(() => {
        toaster('error', 'Login failed');
      });
  };

  return (
    <div className="auth-main-div">
      <Container fluid>
        <Row>
          <Col xs={12} md={6}>
            <div className="authLeftside">
              <Link href="https://roffice.team/">
                <BrandLogo size="sm" href="/" className="loginLogo" />
              </Link>
              <h5 className="loginHeading">Login</h5>
              <p className="loginSubHeading" style={{ marginLeft: '0px' }}>
                Welcome back to ROffice.
              </p>
              <Form
                name="basic"
                initialValues={{ remember: true }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                layout="vertical"
                form={form}
              >
                <Row>
                  <Col lg={6}>
                    <Button className="sso-button" onClick={handleGoogleLogin}>
                      <img src="/auth/google.svg" alt="google.svg" />
                      Login with Google
                    </Button>
                  </Col>
                  <Col lg={6}>
                    <Button className="sso-button" onClick={handleMicroSoftLogin}>
                      <img src="/auth/microsoft.svg" alt="microsoft.svg" />
                      Login with Microsoft
                    </Button>
                  </Col>
                </Row>
                <p className="signup-or">OR</p>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    {
                      required: true,
                      pattern: emailPattern.value,
                      message: emailPattern.message,
                    },
                  ]}
                >
                  <Input placeholder="Enter email address" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    {
                      // Login only needs a non-empty password — credential
                      // validity is Firebase's job. Do NOT enforce the signup
                      // strength pattern here: accounts provisioned outside the
                      // signup form (admins created out-of-band, legacy users)
                      // can have valid passwords that don't match it, and the
                      // pattern would disable the submit button and lock them
                      // out before Firebase is ever consulted.
                      required: true,
                      message: 'Please enter your password',
                    },
                  ]}
                >
                  <Input.Password
                    onPaste={(e) => {
                      e.preventDefault();
                      return false;
                    }}
                    onCopy={(e) => {
                      e.preventDefault();
                      return false;
                    }}
                    placeholder="Password"
                  />
                </Form.Item>
                <Row>
                  <Col xs={6}>
                    <Form.Item name="remember">
                      <Checkbox> Remember me </Checkbox>
                    </Form.Item>
                  </Col>
                  <Col xs={6} className="forgot-password">
                    <Link href="/auth/forgot-password">Forgot password?</Link>
                  </Col>
                </Row>

                <Form.Item>
                  <LoginButton form={form} />
                </Form.Item>
              </Form>
              <div className="back-to-login">
                <p className="mb-2">
                  Don’t have an account? <Link href="/getSupport">Sign up</Link>
                </p>
              </div>
            </div>
          </Col>

          <Col xs={12} md={6} className="authBgImage">
            <div className="authRightside">
              <div className="contentPart">
                <p>
                  “This is an ever changing industry with new technology and processes. But we still
                  needed talented people to get the work done. We welcome you to our platform and
                  the community.”
                </p>
                <h5>Andrew J. Evans</h5>
                <h6>Founder</h6>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
export default Login;
